// Where the login card sends people once they are signed in.
//
// `return` comes off the URL and is therefore attacker-supplied, and the tenant
// switch appends a refresh token to whatever this returns, so the destination
// has to be checked rather than trusted.

const crmEnvOf = (hostname: string): string | null =>
  hostname.match(/(^|\.)(crm|local-crm|dev-crm)\.realadvisor\.com$/)?.[2] ??
  null;

// A CRM host *in the same environment as the page we are on*. Matching any CRM
// host would be too loose: getSwitchUrl (@shared/utils/tenant) appends
// `auth_code=<refresh token>` to this destination, so accepting a dev host from
// a production login page would hand a production refresh token to dev.
const isSameEnvCrmHost = (
  hostname: string,
  currentHostname: string,
): boolean => {
  const env = crmEnvOf(hostname);
  return env != null && env === crmEnvOf(currentHostname);
};

/**
 * Where to send the user once auth has established a session.
 *
 * With no tenant in hand the session is a multitenant one: the token carries no
 * user yet, so the tenant has to be picked on select-tenant, which then forwards
 * to `return` itself.
 */
export const getAfterLoginHref = (
  tenantId: string | null,
  href: string = location.href,
): string => {
  const { searchParams, origin } = new URL(href);
  const rawReturnTo = searchParams.get('return');

  // Follow `return` only onto a CRM host. Everything that sets it legitimately
  // (App.tsx's catch-all, the login forms, the tenant switch) does exactly
  // that; anything else would make the login page an open redirect.
  // main.tsx's getRedirectBackUri applies the same rule to the social path.
  let returnTo: string | null = null;
  if (rawReturnTo != null) {
    try {
      const url = new URL(rawReturnTo, origin);
      if (
        url.origin === origin ||
        isSameEnvCrmHost(url.hostname, new URL(origin).hostname)
      ) {
        returnTo = url.href;
      }
    } catch {
      returnTo = null;
    }
  }

  if (tenantId != null) {
    return returnTo ?? '/';
  }

  const selectTenantUrl = new URL('/en/select-tenant', origin);
  if (returnTo != null) {
    selectTenantUrl.searchParams.set('return', returnTo);
  }
  return selectTenantUrl.href;
};
